import { useEffect, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Storage } from 'aws-amplify';

import { ReactComponent as Trash } from '../../../assets/icons/Trash.svg';
import { dateSchema, positiveSchema, stringSchema, whenStringSchema, zeroSchema } from '../../../services/schemas';
import { electronicInvoiceFields, thirdFields } from '../../../store/catalogs/forms/electronicInvoice';
import { getSpecificConceptsOptionsByKey } from '../../../store/catalogs/puc';
import FormCard from '../FormCard/FormCard';
import FormGrid from '../FormGrid/FormGrid';
import Button from '../../Buttons/Button/Button';
import ImgPDF from '../../ImgPdf/ImgPdf';
import LabelFieldFormik from '../../Fields/LabelField/LabelFieldFormik';
import Select from '../../inputs/Selects/Select/Select';
import { taxesValueLabelList, taxesLabelsObject } from '../../../store/catalogs/taxes';
import NumberInput from '../../inputs/NumberInput/NumberInput';
import utils from '../../../services/utils';

import classes from './ElectronicInvoice.module.scss';

const newThirdKey = 'newThird';

const initialValues = {
  thirdId: '',
  thirdIdentificationType: '',
  thirdIdentificationNumber: '',
  thirdName: '',
  thirdEmail: '',
  thirdBusinessName: '',
  thirdPhone: '',
  thirdAddress: '',
  date: '',
  generalConcept: '',
  dueDate: '',
  specificConcept: '',
  subtotal: 0,
  taxes: [],
  discount: 0,
  totalValue: 0,
  payMethod: '',
  id: '',
  items: [],
  prefixId: '',
  fileLocal: { binary: null, url: null, extension: null, s3FilePath: null }
};

const thirdIdKey = 'thirdId';

const ElectronicInvoice = ({ newValues, onSubmit, third }) => {
  const [generalConcept, setGeneralConcept] = useState(null);

  const { generalConcept: incomingGeneralConcept } = newValues || {};
  useEffect(() => {
    setGeneralConcept(incomingGeneralConcept);
  }, [incomingGeneralConcept]);

  const formSchema = useMemo(
    () =>
      Yup.object().shape({
        thirdId: stringSchema(`Debes escoger el ${third.name}`),
        thirdIdentificationType: whenStringSchema(thirdIdKey, newThirdKey, 'Debes diligenciar el tipo de identificación'),
        thirdIdentificationNumber: whenStringSchema(thirdIdKey, newThirdKey, 'Debes diligenciar la identificación'),
        thirdName: whenStringSchema(thirdIdKey, newThirdKey, 'Debes diligenciar el nombre'),
        date: dateSchema(),
        generalConcept: stringSchema('Diligencia el concepto general'),
        specificConcept: stringSchema('Diligencia el concepto específico'),
        dueDate: dateSchema(),
        subtotal: zeroSchema(),
        taxes: Yup.array().of(
          Yup.object().shape({
            id: Yup.string(),
            value: Yup.number()
          })
        ),
        discount: zeroSchema(),
        totalValue: positiveSchema('El monto total debe ser positivo').test(
          'totalValue',
          'El monto total debe coincidir con el cálculo de los demás valores',
          function (totalValue) {
            const subtotal = this.parent.subtotal;
            const discount = this.parent.discount;
            const taxes = this.parent.taxes;
            return totalValue === subtotal - discount + taxes.reduce((prev, { value }) => prev + value, 0);
          }
        ),
        payMethod: stringSchema(),
        prefixId: stringSchema('Campo requerido'),
        id: stringSchema('Campo requerido'),
        items: Yup.array().of(
          Yup.object().shape({
            description: Yup.string(),
            quantity: Yup.number(),
            measurementUnit: Yup.string(),
            value: Yup.number()
          })
        )
      }),
    [third.name]
  );

  const fields = useMemo(
    () => [
      {
        key: 'third',
        subtitle: third.subtitle,
        array: [
          { ...thirdFields.thirdId, label: third.subtitle, options: [{ value: newThirdKey, label: 'Nuevo' }, ...third.options] },
          thirdFields.thirdIdentificationType,
          thirdFields.thirdIdentificationNumber,
          thirdFields.thirdEmail,
          thirdFields.thirdName,
          thirdFields.thirdBusinessName,
          thirdFields.thirdPhone,
          thirdFields.thirdAddress
        ]
      },
      {
        key: 'purchase',
        subtitle: 'Información de la cuenta de cobro',
        array: [
          electronicInvoiceFields.prefixId,
          electronicInvoiceFields.id,
          electronicInvoiceFields.date,
          electronicInvoiceFields.payMethod,
          electronicInvoiceFields.dueDate
        ]
      },
      {
        key: 'values',
        subtitle: 'Valores',
        array: [electronicInvoiceFields.subtotal, electronicInvoiceFields.discount, electronicInvoiceFields.totalValue]
      },
      {
        key: 'concepts',
        subtitle: 'Conceptos',
        array: [
          electronicInvoiceFields.generalConcept,
          { ...electronicInvoiceFields.specificConcept, options: getSpecificConceptsOptionsByKey(generalConcept) }
        ]
      }
    ],
    [generalConcept, third.options, third.subtitle]
  );

  const setValue = (key, value, setFieldValue) => {
    if (key === 'generalConcept') {
      setGeneralConcept(value);
    }
    setFieldValue(key, value);
  };

  const addTaxHandler = (values, setFieldValue) => {
    setFieldValue('taxes', values.taxes.concat({ id: '01-19', value: 0 }));
  };

  const removeTaxHandler = (idx, values, setFieldValue) => {
    const valuesAux = [...values.taxes];
    valuesAux.splice(idx);
    setFieldValue('taxes', valuesAux);
  };

  const submitHandler = values => {
    const valuesData = { ...values };
    valuesData.taxes = values.taxes.map(({ id: optionId, value }) => {
      const id = optionId.substring(0, 2);
      const name = id === '01' ? 'IVA' : taxesLabelsObject[id];
      const percent = optionId === '01-19' ? 19 : optionId === '01-5' ? 5 : 0;
      return { id, name, percent, value };
    });
    onSubmit(valuesData);
  };

  return (
    <Formik initialValues={newValues || initialValues} validationSchema={formSchema} onSubmit={submitHandler} enableReinitialize>
      {({ values, setFieldValue, isSubmitting }) => {
        const {
          fileLocal: { url, extension }
        } = values;
        return (
          <Form className={`${classes.form}`}>
            <div>
              {fields.map(({ key, subtitle, array }) => {
                let toShow = (
                  <FormGrid onChange={(key, value) => setValue(key, value, setFieldValue)} fields={array} Render={LabelFieldFormik} />
                );
                if (key === 'values') {
                  toShow = (
                    <div>
                      {values.taxes.map(({ id, value }, idx) => (
                        <div className={classes['tax-item']}>
                          <Select
                            options={taxesValueLabelList}
                            value={id}
                            name={idx}
                            onChange={newValue => setFieldValue(`taxes.${idx}.id`, newValue.value)}
                          />
                          <NumberInput
                            name={`${idx}.value`}
                            prefix='$'
                            onChange={number => setFieldValue(`taxes.${idx}.value`, number)}
                            value={value}
                          />
                          <Trash className='pointer' onClick={() => removeTaxHandler(idx, values, setFieldValue)} />
                        </div>
                      ))}
                      <Button className={classes['add-tax']} onClick={() => addTaxHandler(values, setFieldValue)}>
                        Añadir impuesto
                      </Button>
                      <FormGrid
                        onChange={(key, value) => setValue(key, value, setFieldValue)}
                        fields={array}
                        Render={LabelFieldFormik}
                      />
                    </div>
                  );
                }
                return (
                  <FormCard key={key} className={classes['form-box']} subtitle={subtitle}>
                    {toShow}
                  </FormCard>
                );
              })}
              <Button custom='primary' color='purple' type='submit' disabled={isSubmitting}>
                Guardar
              </Button>
            </div>
            {url && <ImgPDF url={url} extension={extension} />}
          </Form>
        );
      }}
    </Formik>
  );
};

export default ElectronicInvoice;

export const getNewValues = async (item, thirdIdName) => {
  let fileURL = null;
  let fileExtension = null;
  if (item.file) {
    fileURL = await Storage.get(item.file);
    const [, extension] = item.file.split('.');
    fileExtension = extension;
  }
  return {
    date: utils.toDateEnd(item.date),
    dueDate: utils.toDateEnd(item.dueDate),
    file: item.file,
    thirdId: item[thirdIdName],
    thirdIdentificationType: '',
    thirdIdentificationNumber: '',
    thirdName: '',
    thirdEmail: '',
    thirdBusinessName: '',
    thirdPhone: '',
    thirdAddress: '',
    generalConcept: item.generalConcept,
    specificConcept: item.specificConcept,
    subtotal: item.subtotal,
    taxes: item.taxes.map(({ id, name, percent, value }) => ({ id: `${id}${name === 'IVA' ? `-${percent}` : ''}`, value })),
    discount: item.discount,
    totalValue: item.totalValue,
    payMethod: item.payMethod,
    currency: item.currency,
    id: item.id,
    items: item.items,
    prefixId: item.prefixId,
    cufe: item.cufe,
    fileLocal: { binary: null, url: fileURL, extension: fileExtension, filePath: item.file }
  };
};

export const processSubmit = (values, thirdKey, thirdIdName) => {
  const {
    date,
    dueDate,
    thirdId,
    thirdIdentificationType,
    thirdIdentificationNumber,
    thirdName,
    thirdEmail,
    thirdBusinessName,
    thirdPhone,
    thirdAddress
  } = values;
  const data = {
    ...values,
    date: utils.toDate(date),
    dueDate: utils.toDate(dueDate)
  };
  const thirdIdNew = thirdId === newThirdKey;
  if (thirdIdNew) {
    data[thirdKey] = {
      name: thirdName,
      businessName: thirdBusinessName,
      identificationType: thirdIdentificationType,
      identificationNumber: thirdIdentificationNumber,
      phone: thirdPhone,
      address: thirdAddress,
      email: thirdEmail,
      id: thirdId
    };
  } else {
    data[thirdIdName] = thirdId;
  }
  return data;
};
